/**
 * Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback =
  typeof globalThis.requestIdleCallback === "function" ? globalThis.requestIdleCallback : setTimeout;

const VIEW_TRANSITION_NAME_PROP = "view-transition-name";
const SUPPORTED_VIEW_TRANSITION_TYPE = "gallery-cross-fade";

/**
 * Read a CSS duration custom property (e.g. `--spring-d220-b0-duration`,
 * `--add-to-cart-dwell-duration`) and return it as milliseconds.
 *
 * Use this from JS that needs to stay in sync with a CSS-tokenized motion
 * tier so token changes propagate to JS-driven animations and dwell timers
 * without a code change. Returns the fallback
 * when the property is unset, malformed, or not a `s`/`ms` value.
 *
 * @param {string} customPropertyName - CSS custom property name including the leading `--`.
 * @param {number} fallbackMs - Returned if the property is unset/malformed.
 * @param {Element} [scope] - Element whose computed style is read (default `:root`).
 *   Pass the component element when a token is meant to be scoped by the cascade
 *   (e.g. the PDP-only add-to-cart dwell).
 * @returns {number} Duration in milliseconds.
 */
export function readDurationToken(customPropertyName, fallbackMs, scope = document.documentElement) {
  const raw = getComputedStyle(scope).getPropertyValue(customPropertyName).trim();
  // Defensive: parseFloat("ms") and parseFloat("s") both return NaN, which
  // would propagate as setTimeout-fires-immediately or implementation-defined
  // WAAPI behavior. Fail closed to the documented fallback instead.
  let parsed = Number.NaN;
  if (raw.endsWith("ms")) {
    parsed = Number.parseFloat(raw);
  } else if (raw.endsWith("s")) {
    parsed = Number.parseFloat(raw) * 1000;
  }
  return Number.isFinite(parsed) ? parsed : fallbackMs;
}

/**
 * Returns a promise that resolves after yielding to the main thread.
 * @see https://web.dev/articles/optimize-long-tasks#scheduler-yield
 */
export const yieldToMainThread = () => {
  if ("scheduler" in globalThis && "yield" in globalThis.scheduler) {
    // @ts-ignore - TypeScript doesn't recognize the yield method yet.
    return globalThis.scheduler.yield();
  }

  return new Promise(resolve => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
};

/**
 * Tells if we are on a low-power device or a constrained network where view transitions
 * would stutter or be visually disruptive. Returns true on low CPU cores, low RAM,
 * Save-Data, slow-2g/2g, or 3g with downlink < 1.5 Mbps.
 *
 * The parser-timed bootstrap is authoritative in storefront documents. The
 * local checks remain only for isolated modules/tests where that bootstrap is
 * intentionally absent.
 *
 * @returns {boolean} True if animation should be skipped, false otherwise
 */
export function isLowPowerDevice() {
  if (Number(navigator.hardwareConcurrency) <= 2) {
    return true;
  }
  if (Number(navigator.deviceMemory) <= 2) {
    return true;
  }
  const conn = navigator.connection;
  if (!conn) {
    return false;
  }
  if (conn.saveData === true) {
    return true;
  }
  if (conn.effectiveType === "slow-2g" || conn.effectiveType === "2g") {
    return true;
  }
  if (conn.effectiveType === "3g" && (conn.downlink ?? 0) < 1.5) {
    return true;
  }
  return false;
}

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean} True if the browser supports View Transitions API, false otherwise
 */
export function supportsViewTransitions() {
  const transitionPrototype = globalThis.ViewTransition?.prototype;
  const typeSetPrototype = globalThis.ViewTransitionTypeSet?.prototype;
  if (typeof document.startViewTransition !== "function" || !transitionPrototype || !typeSetPrototype) {
    return false;
  }

  const transitionMembers = ["types", "finished", "ready", "updateCallbackDone"];
  const hasTransitionMembers = transitionMembers.every(member => member in transitionPrototype);
  const hasTransitionMethods = typeof transitionPrototype.skipTransition === "function";
  const hasTypeSetMethods = typeof typeSetPrototype.add === "function" && typeof typeSetPrototype.clear === "function";
  return (
    hasTransitionMembers &&
    hasTransitionMethods &&
    hasTypeSetMethods &&
    CSS.supports(`selector(:active-view-transition-type(${SUPPORTED_VIEW_TRANSITION_TYPE}))`)
  );
}

/**
 * One shared policy for every same-document transition and fallback animation.
 * Cross-document accent controls are intentionally not part of this policy.
 */
export function viewTransitionsEligible() {
  const root = document.documentElement;
  if (root.dataset.vtDesignMode === "true") {
    return false;
  }
  if (prefersReducedMotion() || isLowPowerDevice()) {
    return false;
  }
  if (
    Number(navigator.maxTouchPoints) > 0 ||
    globalThis.matchMedia("(any-pointer: coarse)").matches ||
    !globalThis.matchMedia("(pointer: fine) and (hover: hover)").matches
  ) {
    return false;
  }
  return supportsViewTransitions();
}

/**
 * Single source of truth for the in-flight view transition state.
 *
 * The canonical state object is owned by the parser-timed bootstrap in
 * `assets/view-transition-bootstrap.js`, which is inlined before any module
 * or deferred coordinator loads. Owning the state there ensures the bridge
 * exists before pagereveal can run.
 *
 * Fields exposed via getters on the bridge:
 *   active     — true iff exactly one transition is currently pinned
 *   finished   — Promise<void> that resolves when the active transition
 *                completes; null when not active
 *   origin     — discriminator: 'cross-doc' | 'same-doc-vt' | 'same-doc-primitive'
 *   pinnedAt   — Date.now() timestamp captured when active flips true (watchdog guard)
 *   current    — read-only alias for `finished` (back-compat for any external
 *                consumer reading `globalThis.__theme_viewTransition.current`)
 *
 * Writer contract (every cross-doc + same-doc writer):
 *   1. Check state.active; if true, drop the incoming transition (first writer wins).
 *   2. Call acquireTransitionPin synchronously — all fields flip in one step.
 *   3. Wrap transition body in try { … } finally { releaseTransitionPin() }.
 *
 * If the bootstrap has not run (e.g., a standalone test harness), install an
 * equivalent fallback bridge here so
 * same-doc consumers (zoom-dialog, empty-cart, and the FLIP filter) still
 * have a working API.
 */
if (!globalThis.__theme_viewTransition) {
  /** @type {{ owner: ThemeViewTransitionOwner | null, active: boolean, finished: Promise<void> | null, origin: string, pinnedAt: number, abort: (() => void) | null }} */
  const _state = { owner: null, active: false, finished: null, origin: "", pinnedAt: 0, abort: null };
  let sequence = 0;
  globalThis.__theme_viewTransition = /** @type {ThemeViewTransitionBridge} */ (
    Object.defineProperties(
      {},
      {
        active: { get: () => _state.active, enumerable: true },
        finished: { get: () => _state.finished, enumerable: true },
        origin: { get: () => _state.origin, enumerable: true },
        pinnedAt: { get: () => _state.pinnedAt, enumerable: true },
        current: { get: () => _state.finished, enumerable: true },
        owner: { get: () => _state.owner, enumerable: false },
        acquireTransitionPin: {
          value: (/** @type {ThemeViewTransitionPinOptions} */ { origin, finished = null, abort = null }) => {
            if (_state.active) {
              return null;
            }
            sequence += 1;
            const owner = Object.freeze({ id: sequence, origin });
            _state.owner = owner;
            _state.pinnedAt = Date.now();
            _state.origin = origin;
            _state.finished = finished;
            _state.abort = abort;
            _state.active = true;
            return owner;
          },
          enumerable: true,
        },
        updateTransitionPin: {
          value: (
            /** @type {ThemeViewTransitionOwner} */ owner,
            /** @type {Pick<ThemeViewTransitionPinOptions, "finished" | "abort">} */ update,
          ) => {
            if (owner !== _state.owner) {
              return false;
            }
            if (Object.hasOwn(update, "finished")) {
              _state.finished = update.finished ?? null;
            }
            if (Object.hasOwn(update, "abort")) {
              _state.abort = update.abort ?? null;
            }
            return true;
          },
          enumerable: true,
        },
        releaseTransitionPin: {
          value: (/** @type {ThemeViewTransitionOwner} */ owner) => {
            if (owner !== _state.owner) {
              return false;
            }
            _state.owner = null;
            _state.active = false;
            _state.finished = null;
            _state.origin = "";
            _state.pinnedAt = 0;
            _state.abort = null;
            return true;
          },
          enumerable: true,
        },
        abortActiveTransition: {
          value: () => {
            if (!_state.owner) {
              return false;
            }
            const owner = _state.owner;
            try {
              _state.abort?.();
            } finally {
              globalThis.__theme_viewTransition.releaseTransitionPin(owner);
            }
            return true;
          },
          enumerable: true,
        },
      },
    )
  );
}

/**
 * The canonical in-flight transition state, exposed for module consumers
 * (Scheduler, watchdog, probe, startViewTransition).
 * Properties are read-only (use acquireTransitionPin / releaseTransitionPin
 * to mutate). Backed by the bridge installed by the critical bootstrap — or by
 * the fallback above when that IIFE did not run.
 */
export const viewTransition = globalThis.__theme_viewTransition;

const WATCHDOG_THRESHOLD_MS = 5000;
const RECOVERY_DELIVERED_EVENT_TIMEOUT_MS = 700;
let watchdogTimer = 0;
/** @type {ThemeViewTransitionOwner | null} */
let watchdogOwner = null;

function clearWatchdog() {
  clearTimeout(watchdogTimer);
  watchdogTimer = 0;
  watchdogOwner = null;
}

/** @param {ThemeViewTransitionOwner} owner */
function armWatchdog(owner) {
  clearWatchdog();
  watchdogOwner = owner;
  watchdogTimer = globalThis.setTimeout(() => {
    if (viewTransition.owner === owner && viewTransition.active) {
      const { origin } = viewTransition;
      const elapsed = Date.now() - viewTransition.pinnedAt;
      viewTransition.abortActiveTransition();
      // Fires when a stuck pin is force-released; listen via
      // globalThis.addEventListener('view-transition:stuck-pin-cleared', handler).
      globalThis.dispatchEvent(
        new CustomEvent("view-transition:stuck-pin-cleared", {
          detail: { elapsed, origin },
        }),
      );
    }
    clearWatchdog();
  }, WATCHDOG_THRESHOLD_MS);
}

/**
 * Synchronously acquire the in-flight transition pin. A second acquire while
 * active is a no-op; the watchdog exists only for the lifetime of its owner.
 * @param {{ origin: string, finished?: Promise<void> | null, abort?: (() => void) | null }} options
 */
export function acquireTransitionPin(options) {
  const owner = viewTransition.acquireTransitionPin(options);
  if (owner) {
    armWatchdog(owner);
  }
  return owner;
}

/** @param {ThemeViewTransitionOwner} owner */
export function releaseTransitionPin(owner) {
  const released = viewTransition.releaseTransitionPin(owner);
  if (released && watchdogOwner === owner) {
    clearWatchdog();
  }
  return released;
}

export const updateTransitionPin = viewTransition.updateTransitionPin;

export function abortActiveTransition() {
  const owner = viewTransition.owner;
  const aborted = viewTransition.abortActiveTransition();
  if (aborted && watchdogOwner === owner) {
    clearWatchdog();
  }
  return aborted;
}

/**
 * @typedef {Object} ViewTransitionContext
 * @property {ParentNode} [scopeElement] Caller-provided scope to constrain element lookups
 *   inside the morph target. Falls back to `document` when omitted. Pages with multiple
 *   instances of a target element (e.g., a `<media-gallery>` in a featured-product section
 *   alongside a separate PDP gallery) need this to disambiguate which instance the
 *   transition should name.
 * @property {Element} [transitionRoot] Element that owns a scoped View Transition.
 *   When supplied, browsers without `Element.startViewTransition()` run the update
 *   directly instead of falling back to a document transition. This keeps modal
 *   transitions from snapshotting the scroll-locked page behind them.
 */

/**
 * Functions to run when a view transition of a given type is started.
 * Receive an optional context bag forwarded from the `startViewTransition` caller; use it
 * when the caller has tighter knowledge of the relevant DOM scope than a global selector.
 * @type {{ [key: string]: (context?: ViewTransitionContext) => (() => void) | undefined | null | Promise<(() => void) | undefined | null> }}
 */
const viewTransitionTypes = {
  "gallery-cross-fade": context => {
    const scope = context?.scopeElement ?? document;
    const gallery = /** @type {import('./media-gallery').MediaGallery | null} */ (scope.querySelector("media-gallery"));
    const slideshow = gallery?.slideshow;
    const slide = /** @type {HTMLElement | null | undefined} */ (slideshow?.slides?.[slideshow.current]);

    if (!slide) {
      return null;
    }

    const slideId = slide.getAttribute("slide-id");
    const name = slideId ? `gallery-image-${slideId}` : `gallery-image-${slideshow.current}`;
    slide.style.setProperty(VIEW_TRANSITION_NAME_PROP, name);

    return () => {
      slide.style.removeProperty(VIEW_TRANSITION_NAME_PROP);
    };
  },
};

/**
 * Dispatch a click to the correct target after re-targeting via elementFromPoint.
 *
 * elementFromPoint can return an inline child (e.g. a <span> inside a <label>),
 * so walk up the DOM with closest() to find the nearest interactive ancestor.
 * If nothing matches, fall back to clicking the raw element — covers custom
 * elements and anything with JS click listeners not in the selector list.
 *
 * @param {Element} target - The element that should receive the click
 * @param {MouseEvent} originalEvent - The original (mis-targeted) click,
 *   read for modifier-key state on link clicks
 */
function dispatchRetargetedClick(target, originalEvent) {
  const link = target.closest("a[href]");

  if (link instanceof HTMLAnchorElement) {
    // Modifier-key routing: ctrl/cmd opens a new browsing context per OS
    // convention. The OS convention for shift+click is "new window," but
    // `globalThis.open(url, "_blank", "noopener")` resolves to a new tab in
    // modern browsers — getting an actual window requires explicit window
    // features, and forcing arbitrary geometry on link activations is worse
    // UX than coalescing to a tab. So we coalesce shift to the same new-tab
    // path as ctrl/cmd. Without this branch, shift+click silently demotes to
    // same-tab navigation on recovered clicks.
    if (originalEvent.button === 1 || originalEvent.ctrlKey || originalEvent.metaKey || originalEvent.shiftKey) {
      globalThis.open(link.href, "_blank", "noopener");
    } else {
      link.click();
    }
    return;
  }

  // <label> is included so label clicks toggle the associated input via its
  // activation behavior. Filter checkboxes and pill filters are wrapped in
  // <label for=...>, so elementFromPoint may return the label itself or a
  // span inside it; walking up to the label restores the expected click.
  const interactive = target.closest("button, [role='button'], input, select, textarea, summary, details, label");
  const liveTarget = /** @type {HTMLElement} */ (interactive || target);
  if (
    originalEvent.button === 1 ||
    originalEvent.metaKey ||
    originalEvent.ctrlKey ||
    originalEvent.shiftKey ||
    originalEvent.altKey
  ) {
    liveTarget.dispatchEvent(
      new MouseEvent(originalEvent.type, {
        bubbles: true,
        cancelable: true,
        button: originalEvent.button,
        clientX: originalEvent.clientX,
        clientY: originalEvent.clientY,
        metaKey: originalEvent.metaKey,
        ctrlKey: originalEvent.ctrlKey,
        shiftKey: originalEvent.shiftKey,
        altKey: originalEvent.altKey,
      }),
    );
  } else {
    liveTarget.click();
  }
}

/**
 * Abort an in-page transition on user interaction and re-target the
 * misrouted click to the real element under the pointer.
 *
 * During a view transition, elements with a `view-transition-name` are inert —
 * the browser skips them for hit-testing. A pointerdown during this window
 * lands on whatever DOM is geometrically behind the inert element, not on the
 * visible snapshot. Even if the transition ends before pointerup, the W3C
 * UIEvents common-ancestor rule corrupts the click target. The symptom on
 * This recovery is intentionally limited to same-document transitions and only
 * retargets a click the browser actually delivered to an absorbing wrapper.
 *
 * @param {ViewTransition} transition
 * @returns {{ finish: () => void, abort: () => void }} Normal completion keeps
 *   an already-armed click candidate alive; forced cleanup removes everything.
 */
// Correlation and cancellation are intentionally colocated so the armed
// pointer sequence has one auditable lifetime.
// eslint-disable-next-line max-lines-per-function
function setupInPageInteractionRecovery(transition) {
  const controller = new AbortController();
  const { signal } = controller;
  let pointerDownObserved = false;
  /** @type {AbortController | null} */
  let recoveryController = null;
  let retargetFrame = 0;
  let finishFrame = 0;

  const handleInteraction = (/** @type {Event} */ event) => {
    if (event.type === "contextmenu" && pointerDownObserved) {
      return;
    }
    if (event.type === "pointerdown") {
      pointerDownObserved = true;
    }
    performance.mark("vt.recovery.skipped");
    transition.skipTransition();

    if (!(event instanceof PointerEvent) || event.type !== "pointerdown") {
      return;
    }
    if ((event.button !== 0 && event.button !== 1) || event.isPrimary === false) {
      return;
    }

    recoveryController?.abort();
    const armedController = new AbortController();
    recoveryController = armedController;
    const recoverySignal = armedController.signal;
    const { button, clientX, clientY } = event;
    const { pointerId, isTrusted, metaKey, ctrlKey, shiftKey, altKey } = event;
    let moved = false;
    let pointerUpObserved = false;
    let listenerTimeout = 0;
    const disarm = () => {
      clearTimeout(listenerTimeout);
      armedController.abort();
      if (recoveryController === armedController) {
        recoveryController = null;
      }
    };
    /** @param {MouseEvent} candidate */
    const modifiersMatch = candidate => {
      if (candidate.isTrusted !== isTrusted) {
        return false;
      }
      if (candidate.metaKey !== metaKey) {
        return false;
      }
      if (candidate.ctrlKey !== ctrlKey) {
        return false;
      }
      if (candidate.shiftKey !== shiftKey) {
        return false;
      }
      return candidate.altKey === altKey;
    };
    /** @param {MouseEvent} candidate */
    const buttonAndModifiersMatch = candidate => candidate.button === button && modifiersMatch(candidate);

    document.addEventListener(
      "pointermove",
      moveEvent => {
        if (moveEvent.pointerId !== pointerId) {
          return;
        }
        const dx = moveEvent.clientX - clientX;
        const dy = moveEvent.clientY - clientY;
        if (dx * dx + dy * dy > 24 * 24) {
          moved = true;
          disarm();
        }
      },
      { capture: true, signal: recoverySignal },
    );
    document.addEventListener(
      "pointercancel",
      cancelEvent => {
        if (!(cancelEvent instanceof PointerEvent) || cancelEvent.pointerId === pointerId) {
          disarm();
        }
      },
      { capture: true, signal: recoverySignal },
    );
    document.addEventListener(
      "pointerup",
      pointerUpEvent => {
        const dx = pointerUpEvent.clientX - clientX;
        const dy = pointerUpEvent.clientY - clientY;
        if (
          pointerUpEvent.pointerId !== pointerId ||
          !buttonAndModifiersMatch(pointerUpEvent) ||
          dx * dx + dy * dy > 24 * 24
        ) {
          disarm();
          return;
        }
        pointerUpObserved = true;
        clearTimeout(listenerTimeout);
        listenerTimeout = globalThis.setTimeout(disarm, RECOVERY_DELIVERED_EVENT_TIMEOUT_MS);
      },
      { capture: true, signal: recoverySignal },
    );
    document.addEventListener(
      "pointerdown",
      newPointerEvent => {
        if (newPointerEvent !== event) {
          disarm();
        }
      },
      { capture: true, signal: recoverySignal },
    );
    document.addEventListener("keydown", disarm, { capture: true, signal: recoverySignal });
    for (const eventName of ["contextmenu", "dragstart"]) {
      document.addEventListener(eventName, disarm, { capture: true, signal: recoverySignal });
    }
    globalThis.addEventListener("pagehide", disarm, { once: true, signal: recoverySignal });

    const deliveredEventName = button === 1 ? "auxclick" : "click";
    document.addEventListener(
      deliveredEventName,
      clickEvent => {
        if (!(clickEvent instanceof MouseEvent)) {
          return;
        }
        const clickPointerId = /** @type {PointerEvent} */ (clickEvent).pointerId;
        const samePointer = !Number.isFinite(clickPointerId) || clickPointerId === pointerId;
        const dx = clickEvent.clientX - clientX;
        const dy = clickEvent.clientY - clientY;
        if (!samePointer || !buttonAndModifiersMatch(clickEvent)) {
          disarm();
          return;
        }
        if (dx * dx + dy * dy > 24 * 24) {
          disarm();
          return;
        }
        disarm();
        if (!pointerUpObserved || moved || clickEvent.defaultPrevented) {
          return;
        }
        const target = /** @type {Element | null} */ (clickEvent.target);
        const isRoot = !target || target === document.documentElement || target === document.body;
        const isKnownWrapper = target?.matches?.(".header__column, .header__columns, overflow-list") ?? false;
        if (!isRoot && !isKnownWrapper) {
          return;
        }
        clickEvent.preventDefault();
        clickEvent.stopImmediatePropagation();

        retargetFrame = requestAnimationFrame(() => {
          retargetFrame = 0;
          const realTarget = document.elementFromPoint(clientX, clientY);
          if (realTarget && realTarget !== document.documentElement && realTarget !== document.body) {
            dispatchRetargetedClick(realTarget, clickEvent);
          }
        });
      },
      { capture: true, signal: recoverySignal },
    );

    // Keep the candidate through a deliberate press, then give the browser a
    // short bounded window to deliver click/auxclick after pointerup. Neither
    // timer ever synthesizes an action.
    listenerTimeout = globalThis.setTimeout(disarm, WATCHDOG_THRESHOLD_MS);
  };

  document.addEventListener("pointerdown", handleInteraction, { capture: true, signal });
  document.addEventListener("keydown", handleInteraction, { capture: true, signal });
  // eslint-disable-next-line @shopify/no-context-menu
  document.addEventListener("contextmenu", handleInteraction, { capture: true, signal });

  const finish = () => {
    // `finished` can settle between pointerdown and click. End only the base
    // transition listeners; an armed delivered-click candidate owns its own
    // bounded lifetime.
    if (signal.aborted) {
      return;
    }
    finishFrame ||= requestAnimationFrame(() => {
      finishFrame = 0;
      controller.abort();
    });
  };

  const abort = () => {
    if (finishFrame) {
      cancelAnimationFrame(finishFrame);
      finishFrame = 0;
    }
    if (retargetFrame) {
      cancelAnimationFrame(retargetFrame);
      retargetFrame = 0;
    }
    controller.abort();
    recoveryController?.abort();
    recoveryController = null;
  };

  return { finish, abort };
}

/**
 * Starts a view transition
 * @param {() => unknown | Promise<unknown>} callback The callback to call when the view transition starts
 * @param {string[]} [types] The types of view transition to use
 * @param {ViewTransitionContext} [context] Optional caller-provided context forwarded to the
 *   per-type setup callbacks in `viewTransitionTypes`. Use it when the caller knows the
 *   correct DOM scope for element lookups (see `ViewTransitionContext` for the shape).
 * @returns {Promise<void>} A promise that resolves when the view transition finishes
 */
// eslint-disable-next-line max-statements
export async function startViewTransition(callback, types, context) {
  /** @param {string} mark */
  const runWithoutTransition = async mark => {
    performance.mark(mark);
    await callback();
  };
  const transitionRoot = context?.transitionRoot;
  const scopedTransitionRoot =
    /** @type {(Element & { startViewTransition?: (options?: StartViewTransitionOptions) => ViewTransition }) | undefined} */ (
      transitionRoot
    );
  const scopedStartViewTransition = scopedTransitionRoot?.startViewTransition;

  if (!viewTransitionsEligible()) {
    let mark = "vt.skip.ineligible";
    if (prefersReducedMotion()) {
      mark = "vt.skip.reduced-motion";
    } else if (isLowPowerDevice()) {
      mark = "vt.skip.low-power";
    }
    await runWithoutTransition(mark);
    return;
  }

  if (transitionRoot && typeof scopedStartViewTransition !== "function") {
    await runWithoutTransition("vt.skip.scoped-unsupported");
    return;
  }

  if (viewTransition.active) {
    await runWithoutTransition("vt.skip.busy");
    return;
  }

  const owner = acquireTransitionPin({ origin: "same-doc-vt" });
  if (!owner) {
    await runWithoutTransition("vt.skip.busy");
    return;
  }

  /** @type {Array<() => void>} */
  const cleanupFunctions = [];
  /** @type {{ finish: () => void, abort: () => void } | null} */
  let interactionRecovery = null;
  /** @type {ViewTransition | null} */
  let transition = null;
  let cleaned = false;
  let forced = false;
  /** @type {() => void} */
  let resolveForced;
  /** @type {Promise<void>} */
  const forcedCleanup = new Promise(resolve => {
    resolveForced = () => resolve();
  });
  const cleanup = () => {
    if (cleaned) {
      return;
    }
    cleaned = true;
    interactionRecovery?.finish();
    for (let index = cleanupFunctions.length - 1; index >= 0; index -= 1) {
      cleanupFunctions[index]?.();
    }
    releaseTransitionPin(owner);
  };
  const forceCleanup = () => {
    if (forced) {
      return;
    }
    forced = true;
    try {
      transition?.skipTransition();
    } finally {
      interactionRecovery?.abort();
      cleanup();
      resolveForced();
    }
  };
  let updateStarted = false;
  const update = async () => {
    updateStarted = true;
    await callback();
  };

  try {
    for (const type of types ?? []) {
      const typeCleanup = viewTransitionTypes[type]?.(context);
      if (typeof typeCleanup === "function") {
        cleanupFunctions.push(typeCleanup);
      }
    }

    performance.mark("vt.same-doc-vt.start");
    try {
      transition =
        transitionRoot && typeof scopedStartViewTransition === "function"
          ? scopedStartViewTransition.call(transitionRoot, { update, types })
          : document.startViewTransition(update);
    } catch {
      if (!updateStarted) {
        await callback();
      }
      return;
    }
    if (!transition) {
      return;
    }
    const activeTransition = transition;

    updateTransitionPin(owner, {
      finished: activeTransition.finished,
      abort: forceCleanup,
    });

    activeTransition.ready.catch(() => {
      // A skipped transition rejects ready by design. Its update still commits.
    });

    if (types?.length && !transitionRoot) {
      if (activeTransition.types && typeof activeTransition.types.add === "function") {
        types.forEach(type => activeTransition.types.add(type));
      } else {
        activeTransition.skipTransition();
      }
    }

    interactionRecovery = setupInPageInteractionRecovery(activeTransition);
    await Promise.race([activeTransition.finished.catch(() => null), forcedCleanup]);
  } finally {
    performance.mark("vt.same-doc-vt.end");
    cleanup();
  }
}

/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}
